import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link2, Unlink, Instagram, Youtube, Music2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";

const META = {
  instagram: { label: "Instagram", icon: Instagram, stat: (s) => `${(s.followers ?? 0).toLocaleString()} followers · ${s.media ?? 0} posts` },
  tiktok: { label: "TikTok", icon: Music2, stat: (s) => `${(s.followers ?? 0).toLocaleString()} followers · ${(s.likes ?? 0).toLocaleString()} likes · ${s.videos ?? 0} videos` },
  youtube: { label: "YouTube", icon: Youtube, stat: (s) => `${(s.subscribers ?? 0).toLocaleString()} subscribers · ${(s.views ?? 0).toLocaleString()} views · ${s.videos ?? 0} videos` },
};

export const SocialConnections = () => {
  const [data, setData] = useState({ connections: [], available: {} });
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/oauth/connections").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (params.get("oauth_linked")) { toast.success(`${META[params.get("oauth_provider")]?.label || "Account"} connected`); setParams({}, { replace: true }); }
    else if (params.get("oauth_error")) {
      const e = params.get("oauth_error");
      toast.error(e === "already_linked_elsewhere" ? "That account is already linked to a different Nexus user." : `Could not connect (${e})`);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const connect = async (p) => {
    setBusy(p);
    try {
      const r = await api.get(`/oauth/${p}/start`, { params: { link: 1 } });
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e.response?.status === 501 ? `${META[p].label} connections are being switched on — check back soon.` : formatApiError(e.response?.data?.detail));
      setBusy("");
    }
  };
  const disconnect = async (p) => {
    setBusy(p);
    try {
      await api.delete(`/oauth/${p}/disconnect`);
      toast.success(`${META[p].label} disconnected — token and cached data deleted`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-3" data-testid="settings-connections-list">
      {Object.keys(META).map((p) => {
        const { label, icon: Icon, stat } = META[p];
        const conn = data.connections.find((c) => c.provider === p);
        return (
          <div key={p} className="flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/5" data-testid={`connection-${p}`}>
            <Icon className="h-5 w-5 text-nexus-pink shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-white">{label}{conn?.handle ? <span className="text-zinc-400 font-normal"> · @{conn.handle.replace(/^@/, "")}</span> : null}</div>
              <div className="text-xs text-zinc-500 truncate" data-testid={`connection-${p}-status`}>
                {conn ? `${stat(conn.stats || {})} · refreshed ${new Date(conn.updated_at).toLocaleDateString("en-GB")}` : "Not connected"}
              </div>
            </div>
            {conn ? (
              <Button variant="outline" size="sm" disabled={busy === p} onClick={() => disconnect(p)} className="rounded-full border-white/15 text-white hover:bg-white/10 hover:text-white" data-testid={`disconnect-${p}-btn`}>
                {busy === p ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <><Unlink className="h-3.5 w-3.5 mr-1.5" /> Disconnect</>}
              </Button>
            ) : (
              <Button size="sm" disabled={busy === p} onClick={() => connect(p)} className="nexus-gradient text-white rounded-full border-0" data-testid={`connect-${p}-btn`}>
                {busy === p ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <><Link2 className="h-3.5 w-3.5 mr-1.5" /> Connect</>}
              </Button>
            )}
          </div>
        );
      })}
      <p className="text-[11px] text-zinc-500">Disconnecting deletes the access token and every cached detail we hold from that network (handle, avatar, follower counts, video list) immediately. Stats are refreshed automatically once a week while connected.</p>
    </div>
  );
};
